import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  useFinishResultDetail,
  useFinishResultsList,
  useFinishGroup
} from '../../api/useGroupFinish';
import { FinishReportPanel } from '../entry/FinishReportPanel';
import { formatUsd, pnlColor } from '../../utils/format';

export const FinishResultsView = () => {
  const [page, setPage] = useState(1);
  const limit = 20;
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data, isLoading } = useFinishResultsList(page, limit);
  const { data: detailData, isLoading: detailLoading } = useFinishResultDetail(
    selectedGroupId ?? ''
  );

  const reFinishMutation = useFinishGroup(selectedGroupId ?? '');

  return (
    <Stack direction="column" spacing={2} sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontSize: '1rem' }}>
        Finished Events
      </Typography>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: '0.7rem' }}>Event</TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }}>Progress</TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }}>Status</TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }}>Net (USD)</TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }}>Kalshi</TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }}>Poly</TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }}>Last run</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.items?.length ? (
                data.items.map(item => {
                  const total = item.computedCount + item.pendingCount;
                  return (
                    <TableRow key={item.groupId} hover>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.8rem' }}>
                          {item.groupName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>
                        {item.computedCount}/{total}
                      </TableCell>
                      <TableCell>
                        {item.fullyFinished ? (
                          <Chip
                            label="fully finished"
                            color="success"
                            size="small"
                            sx={{ fontSize: '0.65rem' }}
                          />
                        ) : (
                          <Chip
                            label="partial"
                            color="warning"
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem' }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{ fontSize: '0.75rem', color: pnlColor(item.groupNetUsd) }}
                        >
                          {formatUsd(item.groupNetUsd)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{ fontSize: '0.75rem', color: pnlColor(item.kalshiNetUsd) }}
                        >
                          {formatUsd(item.kalshiNetUsd)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: pnlColor(item.polymarketNetUsd)
                          }}
                        >
                          {formatUsd(item.polymarketNetUsd)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>
                        {item.lastRunAt
                          ? new Date(item.lastRunAt).toLocaleDateString()
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setSelectedGroupId(item.groupId);
                            setDrawerOpen(true);
                          }}
                        >
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography
                      sx={{
                        fontSize: '0.85rem',
                        color: 'grey.500',
                        textAlign: 'center',
                        py: 3
                      }}
                    >
                      No finished events yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="outlined"
          disabled={page <= 1}
          onClick={() => setPage(p => p - 1)}
        >
          Previous
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={!data || data.items.length < limit}
          onClick={() => setPage(p => p + 1)}
        >
          Next
        </Button>
      </Stack>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: 700, p: 2 } }}
      >
        <Stack direction="column" spacing={2} sx={{ p: 2, height: '100%' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" sx={{ fontSize: '1rem', flex: 1 }}>
              {detailData?.groupName ?? selectedGroupId}
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={() => reFinishMutation.mutate({})}
              disabled={reFinishMutation.isLoading}
            >
              Re-finish
            </Button>
            <Button size="small" onClick={() => setDrawerOpen(false)}>
              Close
            </Button>
          </Stack>

          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : detailData ? (
            <FinishReportPanel snapshot={detailData} />
          ) : (
            <Typography sx={{ fontSize: '0.85rem', color: 'grey.500' }}>
              No snapshot found.
            </Typography>
          )}
        </Stack>
      </Drawer>
    </Stack>
  );
};
